import { TintViewer } from "components/TintViewer"
import { useTranslation } from "react-i18next"
import { ProductV2 } from "types"
import { Box, Button, ModalLayout, Typography } from "@strapi/design-system"
import { PreviewOverlayImageSelector } from "./PreviewOverlayImageSelector"
import { ProductPreview } from "./ProductPreview"
import { customPreviewImage, MODEL_IMAGES } from "./types"
import { useState } from "react"
import { AddCustomImageModalInViewer } from "./AddCustomImageModalInViewer"
import { setCustomPhoto } from "store/user/userSlice"
import { useDispatch } from "react-redux"
import { blobToBase64 } from "utils/zipEffectFileReader"
import { ProductCategories } from "components/Navbar/types"

const TINT_VIEWER_SIZE = 500

interface PreviewOverlayProps {
  productItem: ProductV2
  image: string
  onChangeImage: (url: string | null) => void
  onClose: () => void
  setActiveColorIndex?: (color: number) => void
  effectType?: ProductCategories
  activeColorIndex?: number
}

export const PreviewOverlay = ({
  onClose,
  onChangeImage,
  image,
  productItem,
  setActiveColorIndex,
  effectType = ProductCategories.MAKEUP,
  activeColorIndex = 0,
}: PreviewOverlayProps) => {
  const { t } = useTranslation()
  const [showAddCustomImageModal, setShowAddCustomImageModal] = useState(false)

  const dispatch = useDispatch()

  const handleOpenAddCustomImageModal = () => {
    onChangeImage(customPreviewImage)
    setShowAddCustomImageModal(true)
  }

  const handleCloseAddCustomImageModal = () => {
    setShowAddCustomImageModal(false)
  }
  const handleSaveCustomImage = async (url: string | Blob) => {
    if (typeof url === "string" && url.startsWith("blob:")) {
      try {
        const response = await fetch(url)
        const blob = await response.blob()
        const base64 = await blobToBase64(blob)
        dispatch(setCustomPhoto(base64))
        onChangeImage(base64)
      } catch (error) {
        console.error("Error converting Blob URL to Base64:", error)
      }
    } else if (url instanceof Blob) {
      const imageUrl = URL.createObjectURL(url)

      dispatch(setCustomPhoto(imageUrl))
      onChangeImage(imageUrl)
    } else if (typeof url === "string") {
      dispatch(setCustomPhoto(url))
      onChangeImage(url)
    } else {
      console.error("Invalid type of input")
    }

    setShowAddCustomImageModal(false)
  }

  const handleRemoveCustomImage = async (isActive: boolean) => {
    if (isActive) {
      setShowAddCustomImageModal(true)
      onChangeImage(customPreviewImage)
    }
    dispatch(setCustomPhoto(null))
  }

  const onChangeImageHandler = async (image: string) => {
    if (showAddCustomImageModal) setShowAddCustomImageModal(false)

    onChangeImage(image)
  }

  return (
    <ModalLayout onClose={onClose}>
      <Box
        background="neutral400"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 0,
        }}
      />
      <Box
        style={{
          position: "fixed",
          flexDirection: "column",
          padding: "16px",
          display: "flex",
          gap: "16px",
          right: `calc((100vw - ${TINT_VIEWER_SIZE}px)/2)`,
          bottom: `calc((100vh - ${TINT_VIEWER_SIZE}px + 40px)/2)`,
          width: `${TINT_VIEWER_SIZE}`,
          height: `${TINT_VIEWER_SIZE}`,
        }}
      >
        <Box style={{ position: "absolute", right: "-30px", top: "10px" }}>
          <img
            src={"/close.svg"}
            alt={t("close")}
            style={{ width: "32px", height: "32px", cursor: "pointer" }}
            onClick={onClose}
          />
        </Box>

        {showAddCustomImageModal ? (
          <AddCustomImageModalInViewer
            open={showAddCustomImageModal}
            onClose={handleCloseAddCustomImageModal}
            onSave={handleSaveCustomImage}
            size={TINT_VIEWER_SIZE}
          />
        ) : (
          <TintViewer
            effectType={effectType}
            productItem={productItem}
            activeColor={
              effectType === ProductCategories.MAKEUP
                ? productItem.colors[activeColorIndex].color
                : ""
            }
            image={image || MODEL_IMAGES[0]}
            size={TINT_VIEWER_SIZE}
          />
        )}
      </Box>

      <Box
        background="neutral0"
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "15px 45px",
          gap: "16px",
        }}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <Box
            style={{
              display: "flex",
              alignItems: "start",
              justifyContent: "start",
              flexDirection: "row",
              width: "100%",
            }}
          >
            <ProductPreview
              effectType={effectType}
              data={productItem}
              activeColorIndex={activeColorIndex}
              setActiveColorIndex={setActiveColorIndex}
            />
          </Box>
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "row",
              width: "100%",
              gap: 8,
              textWrap: "nowrap",
            }}
          >
            <Typography> {t("preview")} </Typography>
            <PreviewOverlayImageSelector
              image={image}
              onChange={onChangeImageHandler}
              onOpenAddCustomImageModal={handleOpenAddCustomImageModal}
              onRemoveCustomImage={handleRemoveCustomImage}
            />
          </Box>
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
              flexDirection: "row",
              width: "100%",
            }}
          >
            <Button variant="tertiary" onClick={onClose}>
              {t("close")}
            </Button>
          </Box>
        </Box>
      </Box>
    </ModalLayout>
  )
}
